import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { 
  ChevronLeft,
  Save,
  Plus,
  X,
  Upload,
  Image,
  Menu
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import AdminSidebar from '@/components/admin/AdminSidebar';
import ImageDropzone from '@/components/admin/ImageDropzone';
import { resolveMediaUrl, extractMediaId } from '@/utils/resolveMediaUrl';

const AdminPropertyForm = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!propertyId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    price: '',
    currency: 'CHF',
    location: '',
    area: 'Geneva',
    property_type: 'villa',
    listing_type: 'sale',
    rooms: '',
    bedrooms: '',
    bathrooms: '',
    size_sqm: '',
    land_sqm: '',
    images: [],
    floor_plan: '',
    brochure_url: '',
    features: [],
    status: 'available',
    is_featured: false,
    is_off_market: false,
    is_published: true
  });

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin && isEditing) {
      fetchProperty();
    }
  }, [admin, propertyId]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/properties/${propertyId}`,
        { withCredentials: true }
      );
      const prop = response.data;
      setFormData({
        title: prop.title || '',
        description: prop.description || '',
        short_description: prop.short_description || '',
        price: prop.price?.toString() || '',
        currency: prop.currency || 'CHF',
        location: prop.location || '',
        area: prop.area || 'Geneva',
        property_type: prop.property_type || 'villa',
        listing_type: prop.listing_type || 'sale',
        rooms: prop.rooms?.toString() || '',
        bedrooms: prop.bedrooms?.toString() || '',
        bathrooms: prop.bathrooms?.toString() || '',
        size_sqm: prop.size_sqm?.toString() || '',
        land_sqm: prop.land_sqm?.toString() || '',
        images: prop.images || [],
        floor_plan: prop.floor_plan || '',
        brochure_url: prop.brochure_url || '',
        features: prop.features || [],
        status: prop.status || 'available',
        is_featured: prop.is_featured || false,
        is_off_market: prop.is_off_market || false,
        is_published: prop.is_published !== false
      });
    } catch (error) {
      console.error('Error fetching property:', error);
      setError('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const addImageUrl = () => {
    if (newImageUrl.trim() && !formData.images.includes(newImageUrl.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = async (index) => {
    const url = formData.images[index];
    const mediaId = extractMediaId(url);
    if (mediaId) {
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/media/${mediaId}`, {
          withCredentials: true,
        });
      } catch (err) {
        console.error('Error deleting image from storage:', err);
      }
    }
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImagesUploaded = (urls) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...urls.filter((url) => !prev.images.includes(url))],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = {
      ...formData,
      price: parseInt(formData.price) || 0,
      rooms: formData.rooms ? parseInt(formData.rooms) : null,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseInt(formData.bathrooms) || 0,
      size_sqm: parseInt(formData.size_sqm) || 0,
      land_sqm: formData.land_sqm ? parseInt(formData.land_sqm) : null,
      floor_plan: formData.floor_plan?.trim() || null,
      brochure_url: formData.brochure_url?.trim() || null,
    };

    try {
      if (isEditing) {
        await axios.put(
          `${API_BASE_URL}/api/admin/properties/${propertyId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/api/admin/properties`,
          payload,
          { withCredentials: true }
        );
      }
      navigate('/admin/properties');
    } catch (error) {
      console.error('Error saving property:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(', '));
      } else {
        setError('Failed to save property');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-property-form">
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          showUserInfo={false}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/admin/properties" className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] mb-4">
                <ChevronLeft size={16} />
                Back to Properties
              </Link>
              <h1 className="text-2xl font-heading text-[#1A1A1A]">
                {isEditing ? 'Edit Property' : 'Add New Property'}
              </h1>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="title-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Short Description</label>
                  <input
                    type="text"
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] resize-none"
                    data-testid="description-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Listing Type *</label>
                  <select
                    name="listing_type"
                    value={formData.listing_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="listing-type-select"
                  >
                    <option value="sale">For Sale</option>
                    <option value="rent">For Rent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Property Type *</label>
                  <select
                    name="property_type"
                    value={formData.property_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  >
                    <option value="villa">Villa</option>
                    <option value="apartment">Apartment</option>
                    <option value="penthouse">Penthouse</option>
                    <option value="chalet">Chalet</option>
                    <option value="townhouse">Townhouse</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Price (CHF) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="price-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  >
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                    <option value="reserved">Reserved</option>
                    <option value="rented">Rented</option>
                    <option value="off_market">Off Market</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Location</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Location/City *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Cologny"
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="location-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Area/Region</label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    placeholder="e.g., Geneva"
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  />
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Property Details</h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Rooms</label>
                  <input
                    type="number"
                    name="rooms"
                    value={formData.rooms}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Bedrooms *</label>
                  <input
                    type="number"
                    name="bedrooms"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="bedrooms-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Bathrooms *</label>
                  <input
                    type="number"
                    name="bathrooms"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="bathrooms-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Size (m²) *</label>
                  <input
                    type="number"
                    name="size_sqm"
                    value={formData.size_sqm}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                    data-testid="size-input"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Land Size (m²)</label>
                  <input
                    type="number"
                    name="land_sqm"
                    value={formData.land_sqm}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Images</h2>
              
              <div className="space-y-4">
                <ImageDropzone
                  onUploadComplete={handleImagesUploaded}
                  propertyId={propertyId}
                  disabled={saving}
                />

                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Or enter an external image URL"
                    className="flex-1 px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  />
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="px-4 py-2 bg-[#F5F3EF] text-[#6B6B6B] rounded-sm hover:bg-[#E8E6E1] transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((url, index) => (
                      <div key={`${url}-${index}`} className="relative group">
                        <img
                          src={resolveMediaUrl(url)}
                          alt={`Property ${index + 1}`}
                          className="w-full aspect-video object-cover rounded-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 p-1 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} className="text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Floor Plan URL (optional)</label>
                <input
                  type="text"
                  name="floor_plan"
                  value={formData.floor_plan}
                  onChange={handleChange}
                  placeholder="Enter floor plan image URL (optional)"
                  className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                />
              </div>
              <div className="mt-4">
                <label className="block text-xs uppercase tracking-[0.1em] text-[#9B9B9B] mb-2">Brochure URL (optional)</label>
                <input
                  type="text"
                  name="brochure_url"
                  value={formData.brochure_url}
                  onChange={handleChange}
                  placeholder="Enter brochure or document URL (optional)"
                  className="w-full px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                />
              </div>
            </div>

            {/* Features */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Features</h2>
              
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  placeholder="Add a feature (e.g., Pool, Lake View)"
                  className="flex-1 px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-4 py-2 bg-[#F5F3EF] text-[#6B6B6B] rounded-sm hover:bg-[#E8E6E1] transition-colors"
                >
                  <Plus size={18} />
                </button>
              </div>

              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#F5F3EF] text-sm text-[#6B6B6B] rounded-full"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Visibility Options */}
            <div className="bg-white rounded-sm border border-[#E0DED8] p-6">
              <h2 className="text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-4">Visibility Options</h2>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-[#E0DED8] text-[#8B7355] focus:ring-[#8B7355]"
                  />
                  <span className="text-sm text-[#1A1A1A]">Published (visible on website)</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-[#E0DED8] text-[#8B7355] focus:ring-[#8B7355]"
                  />
                  <span className="text-sm text-[#1A1A1A]">Featured property</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_off_market"
                    checked={formData.is_off_market}
                    onChange={handleChange}
                    className="w-4 h-4 rounded border-[#E0DED8] text-[#8B7355] focus:ring-[#8B7355]"
                  />
                  <span className="text-sm text-[#1A1A1A]">Off-market (only visible to approved users)</span>
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <Link
                to="/admin/properties"
                className="px-6 py-2 border border-[#E0DED8] text-[#6B6B6B] rounded-sm hover:bg-[#F5F3EF] transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2 bg-[#1A1A1A] text-white rounded-sm hover:bg-[#333] transition-colors disabled:opacity-50"
                data-testid="save-button"
              >
                <Save size={16} />
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Property'}
              </button>
            </div>
          </form>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminPropertyForm;
